import React, { createContext, useContext, useMemo, useSyncExternalStore } from 'react';

import createFastStore, { TStoreApi } from './store/createFastStore';

export default function createFastContext<Store>(initialState: Store) {
  const StoreContext = createContext<TStoreApi<Store> | null>(null);

  type TProviderProps = {
    children: React.ReactNode;
    initialState?: Store;
  };
  function Provider({ children, initialState: overrideInitialState }: TProviderProps) {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const value = useMemo(() => createFastStore(overrideInitialState || initialState), []);
    return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
  }

  function useStore<SelectorOutput>(
    selector: (store: Store) => SelectorOutput
  ): [SelectorOutput, (value: Partial<Store>) => void] {
    const store = useContext(StoreContext);
    if (!store) {
      throw new Error('Store not found');
    }

    const state = useSyncExternalStore(
      store.subscribe,
      () => selector(store.get()),
      () => selector(store.get() || initialState)
    );

    return [state, store.set];
  }

  function useStoreRef() {
    const store = useContext(StoreContext);
    if (!store) {
      throw new Error('Store not found');
    }
    return store;
  }

  return {
    Provider,
    useStore,
    useStoreRef,
  };
}
